/**
 * useTokenAuthPage
 *
 * TokenAuthPage의 비즈니스 로직 담당 훅.
 * 활성화 코드 → 생년월일 2단계 인증과 인증 후 목록 페이지 이동을 책임진다.
 */

import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getSessionInfo } from '@/services/authApi';
import { clearAuthToken, clearRefreshToken } from '@/services/api';
import { usePatientAuth } from '@/contexts/PatientAuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import { LAST_SESSION_KEY } from '@/constants/storage';
import { PAGE_TOKEN_AUTH } from '@/constants/translations';
import type { Step1Response, Step2Response } from '@/pages/TokenSurvey/types';
import {
  resolveGuideTargetPath,
  shouldReplaceGuideEntryHistory,
} from '@/utils/guideNavigationResolver';

type AuthStep = 'activation-code' | 'birth-date';

export interface AuthState {
  currentStep: AuthStep;
  step1Token?: string;
  participant?: {
    id: string;
    masked_name?: string;
    external_id: string | null;
  };
}

export interface AuthCodeConfig {
  codeLength: number;
  isAlphanumeric: boolean;
}

export function useTokenAuthPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { settings, applyLanguagePriority } = useSettings();
  const { loginWithToken } = usePatientAuth();
  const t = PAGE_TOKEN_AUTH[settings.language];

  const [state, setState] = useState<AuthState>({
    currentStep: 'activation-code',
  });

  const [authCodeConfig, setAuthCodeConfig] = useState<AuthCodeConfig>({
    codeLength: 4,
    isAlphanumeric: false,
  });

  useEffect(() => {
    if (!token) return;
    sessionStorage.setItem(LAST_SESSION_KEY, token);
    // 진입 시 이전 인증 토큰 클리어 (TokenEntryPage 역할 흡수)
    clearAuthToken();
    clearRefreshToken();
    getSessionInfo(token)
      .then((info) => {
        if (info.valid && info.auth_config) {
          setAuthCodeConfig({
            codeLength: info.auth_config.session_token_length ?? 4,
            isAlphanumeric: info.auth_config.session_token_format === 'alphanumeric',
          });
        }
        // 언어 우선순위 적용
        if (info.language_config) {
          applyLanguagePriority({
            serverLanguage: info.language_config.server_language,
            participantPreferredLanguage: info.language_config.participant_preferred_language,
          }, token);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, [token, applyLanguagePriority]);

  const handleStep1Complete = useCallback((response: Step1Response) => {
    setState({
      currentStep: 'birth-date',
      step1Token: response.step1_token,
      participant: response.participant,
    });
  }, []);

  const handleStep2Complete = useCallback(
    (response: Step2Response) => {
      if (!token) return;

      if (response.access_token) {
        loginWithToken(response.access_token, response.refresh_token ?? '');
      }

      // R7-1: mode='guide' 면 ParticipantGuide 진입.
      // consent 미완료 → /consent, consent 완료 + training 미완료 + CRC_ONLY 아님 → /training
      // 나머지 → /list
      const { mode, project_id, consent_status, training_status, training_performer } = response;
      const entry = searchParams.get('entry');

      const targetPath = resolveGuideTargetPath({
        token,
        mode: mode ?? undefined,
        project_id: project_id ?? undefined,
        consent_status: consent_status ?? undefined,
        training_status: training_status ?? undefined,
        training_performer: training_performer ?? undefined,
        entry: entry ?? undefined,
      });

      navigate(targetPath, {
        replace: shouldReplaceGuideEntryHistory(targetPath),
        state: {
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          participant: response.participant,
        },
      });
    },
    [token, navigate, loginWithToken, searchParams]
  );

  const handleBack = useCallback(() => {
    if (state.currentStep === 'birth-date') {
      setState({ currentStep: 'activation-code' });
    }
  }, [state.currentStep]);

  return {
    token,
    state,
    authCodeConfig,
    handleStep1Complete,
    handleStep2Complete,
    handleBack,
    t,
  };
}
